import { z } from 'zod'
import { listQuerySchema } from './lists.js'
import type { Match } from './matches.js'
import type { CoinEntry, CoinStanding } from './coins.js'
import type { Standing } from '../tournament/standings.js'
import type { Scorer } from '../tournament/stats.js'

/** Ciclo di vita: iscrizioni aperte → chiuse (si sorteggia) → in corso → concluso. Solo azioni dedicate cambiano stato. */
export const TOURNAMENT_STATUSES = ['registration', 'closed', 'active', 'completed'] as const
export type TournamentStatus = (typeof TOURNAMENT_STATUSES)[number]

const cups = z.union([z.literal(3), z.literal(6)])
const coins = z.number().int().min(0).max(10_000)
const singleLine = (max: number) => z.string().trim().min(1).max(max).regex(/^\P{Cc}*$/u)

/** Settaggi modificabili finché il torneo non è `active` (POST/PUT /api/tournaments). Le monete sono valori assoluti; il segno lo dà il motivo. */
export const TournamentSettingsSchema = z.object({
  name: singleLine(100),
  /** Giorno dell'evento, YYYY-MM-DD. */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  playersPerTeam: z.number().int().min(1).max(4).default(2),
  /** null = girone all'italiana; altrimenti K partite per squadra (girone "Champions"). */
  groupMatchesPerTeam: z.number().int().min(1).max(31).nullable().default(null),
  pointsPerWin: z.number().int().min(0).max(10).default(3),
  pointsPerLoss: z.number().int().min(0).max(10).default(0),
  cupsPerTeamGroup: cups.default(3),
  cupsPerTeamFinal: cups.default(6),
  redemptionShot: z.boolean().default(true),
  cupRearrangementAllowed: z.boolean().default(true),
  airballEnabled: z.boolean().default(true),
  coinsInitial: coins.default(100),
  coinsPerCup: coins.default(5),
  coinsPerRedemption: coins.default(10),
  coinsPerAirball: coins.default(10),
  coinsPerWin: coins.default(20),
  coinsPerLoss: coins.default(10),
  coinsPerElimination: coins.default(20),
  coinsThirdPlace: coins.default(20),
  coinsTournamentWin: coins.default(50),
  /** Stockbet (piano 3): tetto per puntata e margine del banco. */
  maxBet: z.number().int().min(1).max(100_000).nullable().default(null),
  bookmakerMargin: z.number().min(0).max(0.5).default(0.05),
  /** Schedina (spec 4): eventi e colonne al massimo, tetto di vincita (null = nessuno), bonus multipla per numero di eventi. */
  stockbetMaxLegs: z.number().int().min(1).max(20).default(10),
  stockbetMaxColumns: z.number().int().min(1).max(1024).default(128),
  stockbetMaxPayout: z.number().int().min(1).max(10_000_000).nullable().default(null),
  stockbetBonus: z
    .array(z.object({ legs: z.number().int().min(2).max(20), pct: z.number().int().min(0).max(100) }))
    .max(10)
    .default([{ legs: 4, pct: 5 }, { legs: 6, pct: 10 }, { legs: 8, pct: 15 }]),
})

export type TournamentSettings = z.infer<typeof TournamentSettingsSchema>

/** Torneo come lo vedono lista e area gestionale. Le date sono ISO string (o null). */
export type Tournament = TournamentSettings & {
  id: string
  status: TournamentStatus
  participantCount: number
  teamCount: number
  registrationClosedAt: string | null
  startedAt: string | null
  completedAt: string | null
  createdAt: string
}

/** GET /api/tournaments: lista pubblica (usata anche dalla DataTable dell'area gestionale). */
export const TournamentListQuerySchema = listQuerySchema(
  ['date', 'name', 'createdAt'],
  { status: z.enum(TOURNAMENT_STATUSES).optional() },
  'desc',
)
export type TournamentListQuery = z.infer<typeof TournamentListQuerySchema>

/** POST /api/tournaments/:id/join. `website` è l'honeypot: un bot che lo compila viene rifiutato. */
export const JoinRequestSchema = z.object({
  name: singleLine(30).pipe(z.string().min(2)),
  website: z.literal('').optional(),
})
export type JoinRequest = z.infer<typeof JoinRequestSchema>

export type Participant = {
  id: string
  tournamentId: string
  name: string
  teamId: string | null
  balance: number
  createdAt: string
}

/** Risposta di join: il token in chiaro esiste solo qui. */
export type JoinResponse = { participant: Participant; token: string }

/** POST /api/tournaments/:id/participants/:pid/coins: rettifica manuale (riga `adjustment`). */
export const CoinAdjustSchema = z.object({
  amount: z.number().int().min(-10_000).max(10_000).refine((n) => n !== 0, 'amount must not be zero'),
  note: singleLine(200),
})
export type CoinAdjust = z.infer<typeof CoinAdjustSchema>

export type Team = {
  id: string
  tournamentId: string
  name: string
  /** Posizione in classifica alla chiusura del girone; null prima. */
  seed: number | null
  players: { id: string; name: string }[]
}

/** GET /api/tournaments/:id: torneo, squadre, iscritti, partite, classifiche e monete. */
export type TournamentDetail = {
  tournament: Tournament
  teams: Team[]
  /** `createdAt`: usato dall'area gestionale per la colonna "Iscritto il" (l'elenco pubblico non la mostra). */
  participants: Pick<Participant, 'id' | 'name' | 'teamId' | 'createdAt'>[]
  matches: Match[]
  standings: Standing[]
  scorers: Scorer[]
  coins: CoinStanding[]
}

/** GET /api/tournaments/:id/me: scheda dell'ospite dietro il suo X-Guest-Token. */
export type GuestMe = { participant: Participant; team: Team | null; entries: CoinEntry[] }

/** POST /api/tournaments/:id/participants/:pid/relink: nuovo token per un iscritto. */
export type RelinkResponse = { token: string }
